import { z } from "zod";

/**
 * Numeric design fields are stored on the wire as numbers, but the
 * DOM `<input type="number">` always emits strings. We model that as a
 * union string|number on the input side, and coerce to number on the
 * output side. zodResolver bridges between the two.
 */
export const designSchema = z.object({
  css: z.string(),
  style: z.object({
    buttonRadius: z.coerce.number().int().min(0).max(100),
    buttonBorder: z.coerce.number().int().min(0).max(20),
    buttonOpacity: z.coerce.number().int().min(0).max(100),
    buttonBorderColour: z.string().min(1),
    buttonFontColour: z.string().min(1),
    buttonBackground: z.string().min(1),
    buttonBoxShadowColour: z.string().min(1),
    fontColour: z.string().min(1),
    background: z.string().min(1),
  }),
});

/** What the form's controlled inputs hold (numerics may be strings from DOM). */
export type DesignFormInput = z.input<typeof designSchema>;
/** What the resolver / submit handler receive (numerics fully coerced). */
export type DesignFormValues = z.output<typeof designSchema>;

export interface DesignPreset {
  name: string;
  style: DesignFormValues["style"];
}

export const DESIGN_PRESETS: DesignPreset[] = [
  {
    name: "Classic",
    style: {
      buttonRadius: 0,
      buttonBorder: 0,
      buttonBorderColour: "#000000",
      buttonFontColour: "#FFFFFF",
      buttonBackground: "#000000",
      buttonBoxShadowColour: "#FFFFFF",
      buttonOpacity: 100,
      fontColour: "#000000",
      background: "#FFFFFF",
    },
  },
  {
    name: "Blue Sea",
    style: {
      buttonRadius: 5,
      buttonBorder: 0,
      buttonBorderColour: "#000000",
      buttonFontColour: "#FFFFFF",
      buttonBackground: "#5ABDF9",
      buttonBoxShadowColour: "#ECF0EF",
      buttonOpacity: 100,
      fontColour: "#000000",
      background: "#ECF0EF",
    },
  },
  {
    name: "Red Hell",
    style: {
      buttonRadius: 20,
      buttonBorder: 0,
      buttonBorderColour: "#000000",
      buttonFontColour: "#FFFFFF",
      buttonBackground: "#DC4353",
      buttonBoxShadowColour: "#F64073",
      buttonOpacity: 100,
      fontColour: "#FFFFFF",
      background: "#F64073",
    },
  },
  {
    name: "Retro",
    style: {
      buttonRadius: 0,
      buttonBorder: 0,
      buttonBorderColour: "#000000",
      buttonFontColour: "#FFFFFF",
      buttonBackground: "#E28AFF",
      buttonBoxShadowColour: "#64FFC0",
      buttonOpacity: 100,
      fontColour: "#3D3B3C",
      background: "#FFFFFF",
    },
  },
];
